import React, { CSSProperties, forwardRef, MouseEvent, useCallback, useRef } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import { Company, Deal, Person } from "@mapmycustomers/shared/types/entity";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { getPreviewEntityUniqueElementId, stopEvents } from "@mapmycustomers/shared/util/browser";
import { AvatarWithName } from "@mapmycustomers/ui";

import ActivityTypeChip from "@app/component/activity/ActivityTypeChip";
import Chip from "@app/component/Chip";
import EntityChip from "@app/component/EntityChip";
import Lexer from "@app/component/Lexer";
import MultilineText from "@app/component/typography/MultilineText";
import { showEntityView } from "@app/store/entityView/actions";
import EntityViewPayload from "@app/store/entityView/EntityViewPayload";
import { getAllTeamsOfEntireOrganization, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import {
  TimelineEventUserMentionedInActivityNote,
  TimelineEventUserMentionedInRecordNote,
} from "@app/types/events/TimelineEvent";

import BaseEvent from "./BaseEvent";
import styles from "./RecordEvent.module.scss";

export interface RecordEventProps {
  event: TimelineEventUserMentionedInActivityNote | TimelineEventUserMentionedInRecordNote;
  expanded: boolean;
  onSizeChange?: () => void;
  style?: CSSProperties;
}

interface Props extends RecordEventProps {
  onShowEntityView: (payload: EntityViewPayload) => void;
  teams: Team[];
  users: User[];
}

const UserMentionedEvent = forwardRef<HTMLDivElement, Props>(
  ({ event, expanded, onShowEntityView, onSizeChange, style, teams, users }, ref) => {
    const intl = useIntl();

    let record: Pick<Company | Deal | Person, "entity" | "id" | "name"> | undefined = undefined;
    if (event.entity.entity === EntityType.ACTIVITY) {
      const association = (event as TimelineEventUserMentionedInActivityNote).associatedEntity?.[0];
      if (association) {
        record = { id: association.id, entity: association.entity, name: association.name };
      }
    } else {
      record = (event as TimelineEventUserMentionedInRecordNote).entity;
    }

    const activityTypeChipRef = useRef<HTMLDivElement>(null);
    const handleActivityClick = useCallback(
      (_, e?: MouseEvent<HTMLElement>) => {
        stopEvents(e);
        const elementId = getPreviewEntityUniqueElementId(activityTypeChipRef.current || undefined);
        onShowEntityView({ elementId, entityId: event.entity.id, entityType: event.entity.entity });
      },
      [event, onShowEntityView]
    );

    const handleShowEntity = useCallback(
      (payload: EntityViewPayload, e?: MouseEvent<HTMLElement>) => {
        stopEvents(e);
        onShowEntityView(payload);
      },
      [onShowEntityView]
    );

    const usersMentioned = event.usersMentioned.length ? event.usersMentioned : event.updatedBy;
    const multipleUsersMentioned = Array.isArray(usersMentioned) && usersMentioned.length > 1;

    return (
      <BaseEvent
        date={event.updatedAt}
        expanded={expanded}
        onSizeChange={onSizeChange}
        ref={ref}
        style={style}
        title={intl.formatMessage(
          {
            id: "dashboard.timeline.event.mentioned",
            defaultMessage:
              "{multipleUsers, select, true {were} other {was}} mentioned on {isActivity, select, true {a {activityType}{record, select, false {} other { with {record}}}} other {a note on {record}}} by {user}",
            description: "Title of the User Mentioned event item",
          },
          {
            activityType:
              event.entity.entity === EntityType.ACTIVITY ? (
                <ActivityTypeChip
                  activityType={
                    (event as TimelineEventUserMentionedInActivityNote).entity.crmActivityType
                  }
                  isAutoEmail={
                    !!(event as TimelineEventUserMentionedInActivityNote).entity.emailLog
                  }
                  onClick={handleActivityClick}
                  ref={activityTypeChipRef}
                />
              ) : (
                ""
              ),
            isActivity: event.entity.entity === EntityType.ACTIVITY,
            multipleUsers: multipleUsersMentioned,
            record: record ? (
              <EntityChip
                entityId={record.id}
                entityType={record.entity}
                inline
                name={record.name}
                onClick={handleShowEntity}
              />
            ) : (
              false
            ),
            user: (
              <Chip inline>
                <AvatarWithName
                  avatarClassName={styles.avatar}
                  className={styles.avatarLabel}
                  user={event.updatedBy}
                />
              </Chip>
            ),
          }
        )}
        user={usersMentioned}
      >
        <div className={styles.noteContainer}>
          <MultilineText wrapLinesWith={Lexer} wrapperProps={{ teams, users }}>
            {event.entity.entity === EntityType.ACTIVITY
              ? (event as TimelineEventUserMentionedInActivityNote).entity.note ?? ""
              : (event as TimelineEventUserMentionedInRecordNote).note.note}
          </MultilineText>
        </div>
      </BaseEvent>
    );
  }
);

const mapStateToProps = (state: RootState) => ({
  teams: getAllTeamsOfEntireOrganization(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  onShowEntityView: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(
  UserMentionedEvent
);
